import { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Loader2, User, Globe, Monitor, Shield, ShieldOff, Clock, X, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Conversation {
  id: string;
  visitor_name: string;
  visitor_email: string | null;
  visitor_phone: string | null;
  status: string;
  current_page: string | null;
  user_agent: string | null;
  is_authenticated: boolean;
  user_id: string | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_id: string | null;
  content: string;
  created_at: string;
}

const parseUserAgent = (ua: string | null) => {
  if (!ua) return { browser: "Desconhecido", os: "Desconhecido", device: "Desktop" };
  const browser = ua.includes("Chrome") ? "Chrome" : ua.includes("Firefox") ? "Firefox" : ua.includes("Safari") ? "Safari" : ua.includes("Edge") ? "Edge" : "Outro";
  const os = ua.includes("Windows") ? "Windows" : ua.includes("Mac") ? "macOS" : ua.includes("Linux") ? "Linux" : ua.includes("Android") ? "Android" : ua.includes("iPhone") ? "iOS" : "Outro";
  const device = ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone") ? "Mobile" : "Desktop";
  return { browser, os, device };
};

interface Props {
  currentUserId: string | null;
}

const AdminVisitorChatTab = ({ currentUserId }: Props) => {
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "open" | "closed">("open");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversations
  useEffect(() => {
    loadConversations();
    const channel = supabase
      .channel("admin-visitor-chats")
      .on("postgres_changes", { event: "*", schema: "public", table: "visitor_chat_conversations" }, () => {
        loadConversations();
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "visitor_chat_messages" }, (payload) => {
        const msg = payload.new as Message;
        if (msg.sender_type === "visitor") {
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            if (selectedConv && msg.conversation_id === selectedConv.id) return [...prev, msg];
            return prev;
          });
          loadConversations();
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    const { data } = await supabase
      .from("visitor_chat_conversations")
      .select("*")
      .order("updated_at", { ascending: false });
    setConversations((data as any[] || []) as Conversation[]);
    setLoading(false);
  };

  const selectConversation = async (conv: Conversation) => {
    setSelectedConv(conv);
    const { data } = await supabase
      .from("visitor_chat_messages")
      .select("*")
      .eq("conversation_id", conv.id)
      .order("created_at", { ascending: true });
    setMessages((data || []) as Message[]);
    // Reset unread
    await supabase.from("visitor_chat_conversations").update({ unread_count: 0 } as any).eq("id", conv.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv || !currentUserId) return;
    setSending(true);
    const { data, error } = await supabase.from("visitor_chat_messages").insert({
      conversation_id: selectedConv.id,
      sender_type: "admin",
      sender_id: currentUserId,
      content: newMessage.trim(),
    }).select().single();
    if (error) {
      toast({ title: "Erro ao enviar", variant: "destructive" });
    } else {
      setMessages(prev => [...prev, data as Message]);
      setNewMessage("");
    }
    setSending(false);
  };

  const closeConversation = async (convId: string) => {
    await supabase.from("visitor_chat_conversations").update({ status: "closed" } as any).eq("id", convId);
    if (selectedConv?.id === convId) setSelectedConv(prev => prev ? { ...prev, status: "closed" } : null);
    loadConversations();
    toast({ title: "Conversa encerrada" });
  };

  const reopenConversation = async (convId: string) => {
    await supabase.from("visitor_chat_conversations").update({ status: "open" } as any).eq("id", convId);
    if (selectedConv?.id === convId) setSelectedConv(prev => prev ? { ...prev, status: "open" } : null);
    loadConversations();
  };

  const filteredConvs = conversations.filter(c => filter === "all" || c.status === filter);
  const openCount = conversations.filter(c => c.status === "open").length;

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Chat de Visitantes</h2>
          <p className="text-xs text-muted-foreground">Responda em tempo real aos visitantes do site</p>
        </div>
        <Badge variant="outline" className="text-xs">
          {openCount} {openCount === 1 ? "conversa aberta" : "conversas abertas"}
        </Badge>
      </div>

      <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Conversation list */}
        <div className="w-80 shrink-0 border border-border rounded-xl bg-card flex flex-col overflow-hidden">
          {/* Filter tabs */}
          <div className="flex border-b border-border p-1 gap-1">
            {(["open", "closed", "all"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === f ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                {f === "open" ? "Abertas" : f === "closed" ? "Fechadas" : "Todas"}
                {f === "open" && openCount > 0 && (
                  <span className="ml-1 bg-primary text-primary-foreground rounded-full px-1.5 text-[9px]">{openCount}</span>
                )}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConvs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
                <MessageSquare className="h-8 w-8 mb-2 opacity-20" />
                <p className="text-xs">Nenhuma conversa</p>
              </div>
            ) : (
              filteredConvs.map(conv => {
                const uaInfo = parseUserAgent(conv.user_agent);
                return (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full text-left p-3 border-b border-border/50 hover:bg-secondary/50 transition-colors ${
                      selectedConv?.id === conv.id ? "bg-primary/5" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          conv.is_authenticated ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                        }`}>
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{conv.visitor_name}</p>
                          <p className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                            <Globe className="h-2.5 w-2.5 shrink-0" />
                            {conv.current_page || "Desconhecida"}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <span className="text-[9px] text-muted-foreground">
                          {formatDistanceToNow(new Date(conv.updated_at), { addSuffix: true, locale: ptBR })}
                        </span>
                        <div className="flex items-center gap-1">
                          {conv.unread_count > 0 && (
                            <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[9px] font-bold">{conv.unread_count}</span>
                          )}
                          <Badge variant={conv.status === "open" ? "default" : "secondary"} className="text-[8px] h-4 px-1">
                            {conv.status === "open" ? "Aberta" : "Fechada"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 ml-10">
                      <span className="text-[9px] text-muted-foreground">{uaInfo.device}</span>
                      <span className="text-[9px] text-muted-foreground">·</span>
                      <span className="text-[9px] text-muted-foreground">{uaInfo.browser}</span>
                      {conv.is_authenticated && (
                        <>
                          <span className="text-[9px] text-muted-foreground">·</span>
                          <span className="text-[9px] text-primary flex items-center gap-0.5">
                            <Shield className="h-2.5 w-2.5" /> Logado
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 border border-border rounded-xl bg-card flex flex-col overflow-hidden">
          {!selectedConv ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mb-3 opacity-20" />
              <p className="text-sm">Selecione uma conversa</p>
              <p className="text-xs text-muted-foreground mt-1">Escolha uma conversa na lista ao lado</p>
            </div>
          ) : (
            <>
              {/* Chat header with visitor info */}
              <div className="p-4 border-b border-border bg-secondary/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedConv(null)} className="lg:hidden p-1 hover:bg-secondary rounded">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedConv.is_authenticated ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    }`}>
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{selectedConv.visitor_name}</p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                        {selectedConv.is_authenticated ? (
                          <><Shield className="h-2.5 w-2.5 text-primary" /> Usuário autenticado</>
                        ) : (
                          <><ShieldOff className="h-2.5 w-2.5" /> Visitante anônimo</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConv.status === "open" ? (
                      <Button size="sm" variant="outline" onClick={() => closeConversation(selectedConv.id)} className="text-xs h-8">
                        <X className="h-3 w-3 mr-1" /> Encerrar
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => reopenConversation(selectedConv.id)} className="text-xs h-8">
                        Reabrir
                      </Button>
                    )}
                  </div>
                </div>

                {/* Visitor metadata */}
                <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-muted-foreground">
                  {selectedConv.current_page && (
                    <div className="flex items-center gap-1 bg-secondary/60 rounded-md px-2 py-1">
                      <Globe className="h-3 w-3" />
                      <span>Página: <strong className="text-foreground">{selectedConv.current_page}</strong></span>
                    </div>
                  )}
                  {selectedConv.user_agent && (() => {
                    const uaInfo = parseUserAgent(selectedConv.user_agent);
                    return (
                      <div className="flex items-center gap-1 bg-secondary/60 rounded-md px-2 py-1">
                        <Monitor className="h-3 w-3" />
                        <span>{uaInfo.os} · {uaInfo.browser} · {uaInfo.device}</span>
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-1 bg-secondary/60 rounded-md px-2 py-1">
                    <Clock className="h-3 w-3" />
                    <span>Início: {new Date(selectedConv.created_at).toLocaleString("pt-BR")}</span>
                  </div>
                  {selectedConv.user_id && (
                    <div className="flex items-center gap-1 bg-primary/10 rounded-md px-2 py-1 text-primary">
                      <Shield className="h-3 w-3" />
                      <span className="font-mono text-[9px]">{selectedConv.user_id.slice(0, 8)}...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <p className="text-xs">Nenhuma mensagem ainda</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-xl px-3 py-2 ${
                        msg.sender_type === "admin"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground"
                      }`}>
                        <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[8px] mt-0.5 ${msg.sender_type === "admin" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                          {msg.sender_type === "admin" ? "Você" : selectedConv.visitor_name} · {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply input */}
              {selectedConv.status === "open" ? (
                <div className="p-3 border-t border-border">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Responder ao visitante..."
                      className="h-10 text-sm"
                    />
                    <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} className="h-10 px-4 bg-primary text-primary-foreground">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-3 border-t border-border text-center">
                  <p className="text-xs text-muted-foreground">Conversa encerrada</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVisitorChatTab;
